/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: VendorForm.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Vendors module.
----------------------------------------------------------------*/

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Factory, Upload, Globe, Link2, Info, Wifi, Search, Plus, Copy } from 'lucide-react';
import * as api from './api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FormBox } from '../../components/ui/FormBox';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { TagInput } from '../../components/ui/TagInput';

interface VendorFormData {
    name: string;
    websiteUrl: string;
    supportUrl: string;
    macPrefixes: string[];
    provisioningProtocols: string[];
    supportsRedirection: boolean;
    notes: string;
    logo?: FileList;
}

// MAC OUI validation pattern (e.g., 00:15:65 or 00-15-65)
const validateMacOui = (value: string): boolean => {
    const patterns = [
        /^[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}:[0-9A-Fa-f]{2}$/,
        /^[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}$/,
        /^[0-9A-Fa-f]{6}$/
    ];
    return patterns.some(pattern => pattern.test(value));
};

// Transform MAC OUI to standard format (XX:XX:XX)
const transformMacOui = (value: string): string => {
    const clean = value.replace(/[:-]/g, '').toUpperCase();
    if (clean.length === 6) {
        return `${clean.slice(0, 2)}:${clean.slice(2, 4)}:${clean.slice(4, 6)}`;
    }
    return value.toUpperCase();
};

export default function VendorForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    // Determine mode
    const isEdit = id && id !== 'new';
    const vendorId = isEdit ? id! : undefined;

    // Form setup
    const { register, trigger, getValues, reset, formState: { errors }, control } = useForm<VendorFormData>({
        defaultValues: {
            name: '',
            websiteUrl: '',
            supportUrl: '',
            macPrefixes: [],
            provisioningProtocols: ['HTTP', 'HTTPS'],
            supportsRedirection: false,
            notes: ''
        }
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewLogo, setPreviewLogo] = useState<string | null>(null);

    // Initial Load
    useEffect(() => {
        if (isEdit && vendorId) {
            loadVendor(vendorId);
        }
    }, [isEdit, vendorId]);

    const loadVendor = async (vid: number | string) => {
        try {
            const vendor = await api.getVendor(vid);
            if (vendor) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const ensureArray = (val: any): string[] => {
                    if (!val) return [];
                    if (Array.isArray(val)) return val;
                    if (typeof val === 'string') {
                        try {
                            const parsed = JSON.parse(val);
                            return Array.isArray(parsed) ? parsed : [];
                        } catch {
                            return [];
                        }
                    }
                    return [];
                };

                reset({
                    name: vendor.name,
                    websiteUrl: vendor.websiteUrl || '',
                    supportUrl: vendor.supportUrl || '',
                    macPrefixes: ensureArray(vendor.macPrefixes),
                    provisioningProtocols: ensureArray(vendor.provisioningProtocols) || ['HTTP', 'HTTPS'],
                    supportsRedirection: vendor.supportsRedirection || false,
                    notes: vendor.notes || ''
                });
                if (vendor.logo) {
                    const normalizedPath = vendor.logo.replace(/\\/g, '/');
                    setPreviewLogo(`/uploads/${normalizedPath}`);
                }
            }
        } catch (error) {
            showError('Failed to load vendor details');
            navigate('/settings/provisioning/provisioning-vendors');
        }
    };

    // Save Handler
    const handleSaveInternal = async () => {
        const isValid = await trigger();
        if (!isValid) return null;

        const data = getValues();
        setIsSubmitting(true);
        try {
            const payload = {
                name: data.name,
                websiteUrl: data.websiteUrl,
                supportUrl: data.supportUrl,
                macPrefixes: data.macPrefixes,
                provisioningProtocols: data.provisioningProtocols,
                supportsRedirection: data.supportsRedirection,
                notes: data.notes
            };

            let savedVendor;
            if (isEdit && vendorId) {
                savedVendor = await api.updateVendor(vendorId, payload);
                if (!savedVendor) savedVendor = { id: vendorId, ...payload };
            } else {
                savedVendor = await api.createVendor(payload);
            }

            showSuccess(`Vendor ${isEdit ? 'updated' : 'created'} successfully`);
            return savedVendor;
        } catch (error) {
            console.error(error);
            showError(`Failed to ${isEdit ? 'update' : 'create'} vendor`);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    // Save with Action Hook
    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/settings/provisioning/provisioning-vendors',
        newPath: '/settings/provisioning/provisioning-vendors/new',
        mode: isEdit ? 'edit' : 'create',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getEditPath: (savedData: any) => `/settings/provisioning/provisioning-vendors/${savedData.id || savedData.uuid}`,
        storageKey: 'vendors:saveAction'
    });

    const handleCancel = () => navigate('/settings/provisioning/provisioning-vendors');

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Factory className="h-5 w-5 text-primary" />}
                title={isEdit ? 'Edit Vendor' : 'New Vendor'}
                backButtonText={t('common.list', 'List')}
                onBack={handleCancel}
                actions={[
                    {
                        icon: <Plus className="h-5 w-5 text-muted-foreground" />,
                        tooltip: t('provisioningVendors.add'),
                        onClick: () => navigate('/settings/provisioning/provisioning-vendors/new')
                    }
                ]}
            />

            <div className="mt-8 space-y-6">
                {/* General Information */}
                <FormBox
                    title="General Information"
                    icon={<Info className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column: Inputs */}
                        <div className="space-y-4">
                            {/* Vendor Name */}
                            <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                                <SimpleTooltip content="Enter the official name of the device manufacturer.">
                                    <Label htmlFor="name" required className="text-left font-medium">
                                        Vendor Name
                                    </Label>
                                </SimpleTooltip>
                                <Input
                                    id="name"
                                    {...register('name', { required: 'Vendor name is required' })}
                                    placeholder="e.g. Yealink"
                                    error={errors.name?.message}
                                />
                            </div>

                            {/* Website URL */}
                            <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                                <SimpleTooltip content="Main website of the manufacturer.">
                                    <Label htmlFor="websiteUrl" className="text-left font-medium">
                                        Website URL
                                    </Label>
                                </SimpleTooltip>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Globe className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <Input
                                        id="websiteUrl"
                                        {...register('websiteUrl')}
                                        className="pl-10"
                                        placeholder="www.example.com"
                                    />
                                </div>
                            </div>

                            {/* Support URL */}
                            <div className="grid grid-cols-[140px_1fr] items-center gap-4">
                                <SimpleTooltip content="Link to firmware downloads, manuals, or support resources.">
                                    <Label htmlFor="supportUrl" className="text-left font-medium">
                                        Support URL
                                    </Label>
                                </SimpleTooltip>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Link2 className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <Input
                                        id="supportUrl"
                                        {...register('supportUrl')}
                                        className="pl-10"
                                        placeholder="support.example.com/downloads"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Logo Display */}
                        <div className="flex flex-col items-center justify-center p-4 bg-muted/20 rounded-lg border border-dashed border-border h-full">
                            {previewLogo ? (
                                <div className="relative w-full h-full flex items-center justify-center">
                                    <img src={previewLogo} alt="Logo" className="max-h-24 w-auto object-contain" />
                                </div>
                            ) : (
                                <div className="text-center p-4">
                                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                                    <span className="text-xs font-medium text-muted-foreground">No Logo Uploaded</span>
                                </div>
                            )}
                            <p className="mt-2 text-[10px] text-muted-foreground text-center max-w-xs">
                                Logo updates handled via seed/backend.
                            </p>
                        </div>
                    </div>
                </FormBox>

                {/* MAC Address Prefixes (OUI) */}
                <FormBox
                    title="MAC Address Prefixes (OUI)"
                    icon={<Wifi className="h-5 w-5" />}
                    defaultCollapsed={false}
                >
                    <div className="space-y-2">
                        <SimpleTooltip content="Enter Vendor MAC Prefixes (OUI). Format: XX:XX:XX (e.g., 00:15:65). These are used to auto-detect the manufacturer.">
                            <Label className="text-left font-medium">
                                MAC Prefixes
                            </Label>
                        </SimpleTooltip>
                        <Controller
                            name="macPrefixes"
                            control={control}
                            render={({ field }) => (
                                <TagInput
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Enter MAC prefix (e.g., 00:15:65)"
                                    validate={validateMacOui}
                                    transform={transformMacOui}
                                    errorMessage="Invalid MAC OUI format. Use XX:XX:XX, XX-XX-XX, or XXXXXX"
                                />
                            )}
                        />
                    </div>
                </FormBox>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                isEditMode={isEdit}
                isSaving={isSubmitting}
                cancelText={t('common.cancel', 'Cancel')}
                useSaveDropdown={true}
                saveDropdownStorageKey="vendors:saveAction"
            />
        </div>
    );
}
